import {
  IlogisticServiceDetails,
  IlogisticServiceDetailsToolsData,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../../../../types/services';
import LogisticServiceToolCard from '../LogisticServiceToolCard/LogisticServiceToolCard';
import NudosStaticAlertInComponent from '../../../../../../../components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import { BordOneToneIcon } from 'components/NudosComponents';
import { bordColorTheme } from 'styles/styles.constants';
import { getSectionsTexts, replaceOriginLocationPlaceholder, separateTools } from './utils';

import './OffboardingWarehouseToolsGroup.scss';

interface IOffboardingWarehouseToolsGroupProps {
  tools: IlogisticServiceDetailsToolsData[];
  logisticServiceData: IlogisticServiceDetails;
  updateDataCallback: () => Promise<void>;
}

/**
 * Componente que muestra las herramientas agrupadas en secciones para servicios offboarding con destino a bodega.
 */
export const OffboardingWarehouseToolsGroup = ({
  tools,
  logisticServiceData,
  updateDataCallback
}: IOffboardingWarehouseToolsGroupProps) => {
  const { originLocation, statusName, collectionInfo } = logisticServiceData;

  const isDelivered = statusName === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED;
  const originLocationName = replaceOriginLocationPlaceholder(originLocation?.place, collectionInfo);
  const sectionTexts = getSectionsTexts(isDelivered, originLocationName);

  const { deliveredRegularTools, deliveredAdditionalTools, notDeliveredRegularTools, notDeliveredAdditionalTools } =
    separateTools(tools);

  const hasDeliveredTools = deliveredRegularTools.length > 0 || deliveredAdditionalTools.length > 0;
  const hasNotDeliveredTools = notDeliveredRegularTools.length > 0 || notDeliveredAdditionalTools.length > 0;

  const renderToolCard = (tool: IlogisticServiceDetailsToolsData) => (
    <LogisticServiceToolCard
      key={`detail-card-${tool.serial || tool.productId}-${tool.serial}`}
      toolData={tool}
      logisticServiceData={logisticServiceData}
      updateDataCallback={updateDataCallback}
    />
  );

  return (
    <div className="offboardingWarehouseToolsGroup">
      {hasDeliveredTools && (
        <div className="sectionTitleContainer">
          <BordOneToneIcon variant="checkCircle" standardSize={14} stroke={bordColorTheme.green[600]} />
          <span className="sectionTitleDeliveredText">{sectionTexts.deliveredSectionTitle}</span>
        </div>
      )}

      {deliveredRegularTools.length > 0 && (
        <div className="toolsSection">
          <div className="toolsContainer">{deliveredRegularTools.map(tool => renderToolCard(tool))}</div>
        </div>
      )}

      {deliveredAdditionalTools.length > 0 && (
        <div className="toolsSection">
          <p className="sectionTitleAdditional">{sectionTexts.additionalDeliveredSectionTitle}</p>
          <div className="toolsContainer">{deliveredAdditionalTools.map(tool => renderToolCard(tool))}</div>
        </div>
      )}

      {hasDeliveredTools && hasNotDeliveredTools && <div className="toolsDivider" />}

      {hasNotDeliveredTools && (
        <>
          <div className="sectionTitleContainer">
            <BordOneToneIcon variant="xCircle" standardSize={14} stroke="#F00" />
            <span className="sectionTitleNotDeliveredText">{sectionTexts.notDeliveredSectionTitle}</span>
          </div>
          <NudosStaticAlertInComponent
            alertOptions={{
              subtitle: <span dangerouslySetInnerHTML={{ __html: sectionTexts.notDeliveredNotificationMessage }} />
            }}
            type="warning"
            color="yellow"
            customClassname="notDeliveredAlert"
          />
        </>
      )}

      {notDeliveredRegularTools.length > 0 && (
        <div className="toolsSection">
          <div className="toolsContainer">{notDeliveredRegularTools.map(tool => renderToolCard(tool))}</div>
        </div>
      )}

      {notDeliveredAdditionalTools.length > 0 && (
        <div className="toolsSection">
          <p className="sectionTitleAdditional">{sectionTexts.additionalNotDeliveredSectionTitle}</p>
          <div className="toolsContainer">{notDeliveredAdditionalTools.map(tool => renderToolCard(tool))}</div>
        </div>
      )}
    </div>
  );
};
